const Konashi = require('./KonashiServo');

class _PioPin {
    constructor (pin) {
        this.number = pin;
        this.pioMode = Konashi.consts.INPUT;
        this.pullup = Konashi.consts.NO_PULLS;
        this.input = Konashi.consts.LOW;
        this.output = Konashi.consts.LOW;
        this.pwmMode = Konashi.consts.KONASHI_PWM_DISABLE;
        this.pwmPeriod = 0;
        this.pwmDuty = 0;
        this.pwmRatio = 0;
    }
}

class _PwmPin {
    constructor (pin) {
        this.number = pin;
        this.pwmMode = Konashi.consts.KONASHI_PWM_DISABLE;
        this.pwmDuty = 0;
    }
}

const BLE_DELAY = {
    NONE: 0,
    NORMAL: 40,
    LONG: 100,
    READ: 200,
    DEBUG: 1000
};

class Queue {
    constructor () {
        let queue = Promise.resolve(true);

        const add = function (job, delay) {
            const p = function () {
                return new Promise(resolve => {
                    setTimeout(() => {
                        job();
                        resolve();
                    }, delay);
                });
            };
            queue = queue.then(p);
        };

        this.add = add;
    }
}

class CocoroServo {
    constructor () {
        this._queue = new Queue();
        this._konashi = null;
        this._name = null;

        this._pioPins = [new _PioPin(0), new _PioPin(1), new _PioPin(2), new _PioPin(3), new _PioPin(4)];
        this._pwmPins = [new _PwmPin(0), new _PwmPin(1), new _PwmPin(2), new _PwmPin(3)];
    }

    getConsts () {
        return {
            PIO0: Konashi.consts.PIO0,
            PIO1: Konashi.consts.PIO1,
            PIO2: Konashi.consts.PIO2,
            PIO3: Konashi.consts.PIO3,
            PIO4: Konashi.consts.PIO4,
            PWM0: Konashi.consts.PWM0,
            PWM1: Konashi.consts.PWM1,
            PWM2: Konashi.consts.PWM2,
            PWM3: Konashi.consts.PWM3,
            LOW: Konashi.consts.LOW,
            HIGH: Konashi.consts.HIGH,
            PWM_DISABLE: Konashi.consts.KONASHI_PWM_DISABLE, // this is for hard PWM not soft PWM
            PWM_ENABLE: Konashi.consts.KONASHI_PWM_ENABLE // this is for hard PWM not soft PWM
        };
    }

    connect (prefix) {
        if (this._konashi) this._konashi.disconnect();

        return Konashi.funcs
            .find(true, prefix)
            .then(k => {
                this._konashi = k;
                this._name = k.name();
                this._connected = true;
            })
            .then(() => {
                this._startDigitalRead();

                const that = this;
                this._queue.add(() => {
                    that.setHardPwmDutyAll(1450, 1450, 1450, 1450);
                }, BLE_DELAY.LONG);
            });
    }

    reset () {
        this._konashi.softwareReset();

        for (const i in this._pioPins) {
            this._pioPins[i] = new _PioPin(i);
        }

        for (const i in this._pwmPins) {
            this._pwmPins[i] = new _PwmPin(i);
        }
    }

    dispose () {
        this._stopDigitalRead();
    }

    isConnected () {
        let connected = false;
        if (this._konashi) {
            connected = this._konashi.isConnected();
        }
        return connected;
    }

    /**
   * @param {number} pid Konashi.consts.PIOX
   * @param {number} mode Konashi.consts.INPUT|OUTPUT
   */
    _setMode (pid, mode) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 4) return;
        if (mode == this._pioPins[pid].pioMode) return;

        const that = this;
        this._queue.add(() => {
            that._konashi.pinMode(pid, mode);
            that._pioPins[pid].pioMode = mode;
        }, BLE_DELAY.LONG);
    }

    /**
   * Confirm if the pin mode is Konashi.consts.OUTPUT before call this function.
   *
   * @param {Konashi.consts.PIOX} pid PIO pin number (0-4)
   * @param {Konashi.consts.HIGI|LOW} value output value
   */
    setOutput (pid, value) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 4) return;

        let isModeChanged = false;

        if (this._pioPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_DISABLE) {
            this._setPwmMode(pid, Konashi.consts.KONASHI_PWM_DISABLE);
            isModeChanged = true;
        }

        if (this._pioPins[pid].pioMode == Konashi.consts.INPUT) {
            this._setMode(pid, Konashi.consts.OUTPUT);
            isModeChanged = true;
        }

        if (!isModeChanged && this._pioPins[pid].output == value) {
            return;
        }

        const that = this;
        this._queue.add(() => {
            that._konashi.digitalWrite(pid, value);
            that._pioPins[pid].output = value;
        }, BLE_DELAY.NORMAL);
    }

    /**
   * @param {*} pid PIO pin number (0-4)
   * @param {*} mode Konashi.consts.KONASHI_PWM_DISABLE|KONASHI_PWM_ENABLE|KONASHI_PWM_ENABLE_LED_MODE
   */
    _setPwmMode (pid, mode) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 4) return;
        if (mode == this._pioPins[pid].pwmMode) return;

        const that = this;
        this._queue.add(() => {
            that._konashi.pwmMode(pid, mode);
            that._pioPins[pid].pwmMode = mode;
        }, BLE_DELAY.LONG);
    }

    /**
   * @param {*} pid PIO pin number (0-4)
   * @param {Int} ratio 0 - 100
   */
    pwmLedDrive (pid, ratio) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 4) return;

        let isModeChanged = false;

        if (this._pioPins[pid].pioMode == Konashi.consts.INPUT) {
            this._setMode(pid, Konashi.consts.OUTPUT);
        }

        if (this._pioPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_ENABLE_LED_MODE) {
            this._setPwmMode(pid, Konashi.consts.KONASHI_PWM_ENABLE_LED_MODE);
            this._pwmPeriod(pid, Konashi.consts.KONASHI_PWM_LED_PERIOD);

            isModeChanged = true;
        }

        if (!isModeChanged && ratio == this._pioPins[pid].pwmRatio) {
            return;
        }

        const that = this;
        this._queue.add(() => {
            that._konashi.pwmLedDrive(pid, ratio);
            that._pioPins[pid].pwmRatio = ratio;
        }, BLE_DELAY.NORMAL);
    }

    /**
   * @param {*} pid PIO pin number (0-4)
   * @param {*} period PWM period in microseconds
   */
    _pwmPeriod (pid, period) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 4) return;

        const that = this;
        this._queue.add(() => {
            that._konashi.pwmPeriod(pid, period);
            that._pioPins[pid].pwmPeriod = period;
        }, BLE_DELAY.NORMAL);
    }

    /**
   * @param {*} pid PIO pin number (0-4)
   * @param {*} duty PWM duty in microseconds
   */
    _pwmDuty (pid, duty) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 4) return;

        if (this._pioPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_ENABLE) {
            this._setPwmMode(pid, Konashi.consts.KONASHI_PWM_ENABLE);
        } else if (duty == this._pioPins[pid].pwmDuty) {
            return;
        }

        const that = this;
        this._queue.add(() => {
            that._konashi.pwmDuty(pid, duty);
            that._pioPins[pid].pwmDuty = duty;
        }, BLE_DELAY.NORMAL);
    }

    /**
   * @param {*} pid PIO pin number (0-4)
   * @returns {number} stored digital input value (Konashi.consts.HIGH|LOW)
   */
    getDigitalValue (pid) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 4) return;

        return this._pioPins[pid].input;
    }

    /**
   * @param {*} pid PIO pin number (0-4)
   */
    digitalRead (pid) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 4) return;
        if (this._pioPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_DISABLE) {
            this._setPwmMode(pid, Konashi.consts.KONASHI_PWM_DISABLE);
        }

        if (this._pioPins[pid].pioMode == Konashi.consts.OUTPUT) {
            this._setMode(pid, Konashi.consts.INPUT);
        }

        const that = this;
        this._konashi.digitalRead(pid).then(v => {
            that._pioPins[pid].input = v; // Konashi.consts.HIGH || Konashi.consts.LOW
        });
    }

    _startDigitalRead () {
        const that = this;
        this._onNotified = function (data) {
            // data is Number (0bXXXXXXXX)
            that._pioPins[3].input = (data >> 3) & 0x01; // 0: LOW, 1: HIGH
            that._pioPins[4].input = (data >> 4) & 0x01; // 0: LOW, 1: HIGH

            console.log('DEBUG'); // DEBUG for チャタリング
        };

        if (!this._konashi) return;
        this._konashi.startDigitalInputNotification(this._onNotified);
    }

    _stopDigitalRead () {
        this._onNotified = null;
        this._konashi.stopDigitalInputNotification();
    }

    // { Hard PWM
    /**
   * @param {*} pid 0 - 3
   * @param {*} mode Konashi.consts.KONASHI_PWM_DISABLE|KONASHI_PWM_ENABLE
   */
    _setHardPwmMode (pid, mode) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 3) return;
        if (mode == this._pwmPins[pid].pwmMode) return;

        const that = this;
        this._queue.add(() => {
            that._konashi.hardPwmMode(pid, mode);
            that._pwmPins[pid].pwmMode = mode;
        }, BLE_DELAY.LONG);
    }

    /**
   * @param {*} pid PWM pin number (0-3)
   * @param {*} duty PWM duty in microseconds
   */
    setHardPwmDuty (pid, duty) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 3) return;

        if (this._pwmPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_ENABLE) {
            this._setHardPwmMode(pid, Konashi.consts.KONASHI_PWM_ENABLE);
        } else if (duty == this._pwmPins[pid].pwmDuty) {
            return;
        }

        const that = this;
        this._queue.add(() => {
            that._konashi.hardPwmDuty(pid, duty);
            that._pwmPins[pid].pwmDuty = duty;
        }, BLE_DELAY.NORMAL);
    }

    /**
   *
   * @param {number} duty0 PWM0 duty in microseconds
   * @param {number} duty1 PWM1 duty in microseconds
   * @param {number} duty2 PWM2 duty in microseconds
   * @param {number} duty3 PWM3 duty in microseconds
   */
    setHardPwmDutyAll (duty0, duty1, duty2, duty3) {
        if (!this._konashi) return;

        const that = this;
        this._queue.add(() => {
            that._konashi.hardPwmModeAll(0b00001111);
            for (let i = 0; i < that._pwmPins.length; i++) {
                that._pwmPins[i].pwmMode = Konashi.consts.KONASHI_PWM_ENABLE;
            }
        }, BLE_DELAY.NORMAL);

        this._queue.add(() => {
            that._konashi.hardPwmDutyAll(duty0, duty1, duty2, duty3);
            that._pwmPins[0].pwmDuty = duty0;
            that._pwmPins[1].pwmDuty = duty1;
            that._pwmPins[2].pwmDuty = duty2;
            that._pwmPins[3].pwmDuty = duty3;
        }, BLE_DELAY.NORMAL);
    }

    /**
   * @param {*} pid PWM pin number (0-3)
   * @param {*} duty PWM duty in microseconds
   * @param {*} duration duration in milliseconds (0-65535)
   */
    setHardPwmDutyTime (pid, duty, duration) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 3) return;

        if (this._pwmPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_ENABLE) {
            this._setHardPwmMode(pid, Konashi.consts.KONASHI_PWM_ENABLE);
        } else if (duty == this._pwmPins[pid].pwmDuty) {
            return;
        }

        const that = this;
        this._queue.add(() => {
            that._konashi.hardPwmDutyTime(pid, duty, duration);
            that._pwmPins[pid].pwmDuty = duty;
        }, BLE_DELAY.NORMAL);
    }

    // Hard PWM }
}

module.exports = CocoroServo;
